/* eslint-disable no-nested-ternary, no-underscore-dangle,  prefer-arrow-callback */

import { withStyles } from '@material-ui/core/styles'
import ArrowDropDown from '@material-ui/icons/ArrowDropDown'
import cn from 'classnames'
import moment from 'moment-timezone/builds/moment-timezone-with-data'
import React from 'react'
import Select from 'react-select'

import theme from '../../utils/theme'
import LabelWrapper from '../LabelWrapper'

const styles = theme => ({
  timeZone: {
    '& .wrapped:not(.bordered)': {
      borderBottom: theme.borders.default,
      '& .tz-select__control': {
        border: 'none',
        backgroundColor: `var(--gameday-inputField-inputBGColor, ${theme.inputField.inputBGColor})`,
      },
      '& .tz-select__single-value--is-disabled': {
        color: `var(--gameday-inputField-inputTextColorDisabled, ${theme.inputField.inputTextColorDisabled})`,
      },
      '& .tz-select-wrapper.tz-select--is-disabled': {
        pointerEvents: 'all',
        cursor: 'not-allowed',
        '&:hover': {
          backgroundColor: theme.inputField.inputBGColorDisabled,
        },
        '& > div': {
          pointerEvents: 'none',
        },
      },
      '& .tz-select__control:hover, & .tz-select-wrapper:focus-within .tz-select__control': {
        backgroundColor: `var(--gameday-inputField-inputBGHover, ${theme.inputField.inputBGHover})`,
      },
      '&.has-error': {
        borderColor: theme.status.errorBorder,
      },
    },
    '& .wrapped:not(.full-width) .tz-select-wrapper': {
      maxWidth: '280px',
      width: '100%',
    },
    '&:focus-within .wrapped:not(.bordered)': {
      borderColor: theme.borders.borderColorActive,
    },
    '& .has-error.bordered .tz-select__control': {
      borderColor: theme.status.errorBorder,
      background: theme.status.errorBG,
    },
    '& .tz-select__control': {
      height: '36px',
      minHeight: 'unset',
      '&.tz-select__control--is-focused': {
        borderColor: theme.borders.borderColorActive,
        boxShadow: 'none',
        alignItems: 'flex-start',
      },
      '&.tz-select__control--is-disabled': {
        color: `var(--gameday-inputField-inputTextColorDisabled, ${theme.inputField.inputTextColorDisabled})`,
        backgroundColor: `var(--gameday-inputField-inputBGDisabled, ${theme.inputField.inputBGDisabled})`,
      },
      '& .tz-select__single-value--is-disabled': {
        color: `var(--gameday-inputField-inputTextColorDisabled, ${theme.inputField.inputTextColorDisabled})`,
      },
    },
    '& .tz-select-wrapper': {
      width: props => props.customWidth && props.customWidth,
    },
    '& .tz-select__value-container': {
      height: '34px',
      padding: '0 0 0 0.625rem',
      fontSize: theme.typography.fontSizeSm,
      '&  > div:last-child': {
        margin: 0,
        padding: 0,
      },
      '&:hover': {
        cursor: 'text',
        '& .tz-select__placeholder': {
          color: `var(--gameday-inputField-inputTextColor, ${theme.inputField.inputTextColor})`,
        },
      },
      '&:focus-within .tz-select__single-value': {
        fontWeight: theme.typography.fontWeightBolder,
      },
    },
    '& .tz-select__placeholder': {
      color: `var(--gameday-inputField-inputPlaceholderColor, ${theme.inputField.inputPlaceholderColor})`,
      '&:hover': {
        color: `var(--gameday-inputField-inputTextColor, ${theme.inputField.inputTextColor})`,
      },
    },
    '& .tz-select__input input': {
      height: '34px',
    },
    '& .tz-select__indicators': {
      padding: '0 2px',
      '& .tz-select__indicator': {
        padding: 0,
      },
    },
    '& .tz-select__indicator-separator': {
      display: 'none',
    },
    '& .tz-select__option': {
      color: `var(--gameday-inputField-inputTextColor, ${theme.inputField.inputTextColor})`,
      '&.tz-select__option--is-selected': {
        backgroundColor: `var(--gameday-inputField-inputBGHover, ${theme.inputField.inputBGHover})`,
        color: `var(--gameday-inputField-inputTextColor, ${theme.inputField.inputTextColor})`,
      },
      '&.tz-select__option--is-focused': {
        backgroundColor: `var(--gameday-inputField-inputBGHover, ${theme.inputField.inputBGHover})`,
      },
    },
  },
  timezoneSelectControl: {
    '& .tz-select__single-value': {
      color: `var(--gameday-inputField-inputTextColor, ${theme.inputField.inputTextColor})`,
    },
    '& .tz-select__control, & .tz-select__control:hover': {
      borderColor: `var(--gameday-borders-borderColor, ${theme.borders.borderColor})`,
      backgroundColor: `var(--gameday-inputField-inputBGColor, ${theme.inputField.inputBGColor})`,
    },
  },
  arrow: {
    color: theme.inputField.iconColor,
  },
})

const customDropDownStyles = {
  menuPortal: base => ({
    ...base,
    zIndex: 9999,
  }),
  menu: base => ({
    ...base,
    backgroundColor: `var(--gameday-inputField-inputBGColor, ${theme.inputField.inputBGColor})`,
  }),
  option: (base, state) => ({
    ...base,
    color: `var(--gameday-inputField-inputTextColor, ${theme.inputField.inputTextColor})`,
    backgroundColor: state.isSelected
      ? `var(--gameday-inputField-inputBGHover, ${theme.inputField.inputBGColor})`
      : state.isFocused
        ? `var(--gameday-inputField-inputBGHover, ${theme.inputField.inputBGColor})`
        : undefined,
  }),
  input: base => ({
    ...base,
    color: 'red',
  }),
}

let zones = [...moment.tz.names(), 'Europe/Amsterdam']
zones = [...new Set(zones)]

zones = zones
  .filter(tz =>
    tz.match(/^(((Africa|America|Antarctica|Asia|Australia|Europe|Arctic|Atlantic|Indian|Pacific)\/.+)|(UTC))$/)
  )
  .reduce((memo, tz) => {
    memo.push({
      name: tz,
      timezone: moment.tz(tz).format('Z'),
      offset: moment.tz(tz).utcOffset(),
    })

    return memo
  }, [])

const utcTZCode = 'Etc/UTC'
const gmtTZCode = 'Etc/GMT'

if (moment.tz.names().filter(tz => tz.match(utcTZCode)).length > 0) {
  zones.push({
    name: moment.tz.names().filter(tz => tz.match(utcTZCode))[0],
    timezone: moment.tz(utcTZCode).format('Z'),
    offset: moment.tz(utcTZCode).utcOffset(),
  })
}

if (moment.tz.names().filter(tz => tz.match(gmtTZCode)).length > 0) {
  zones.push({
    name: moment.tz.names().filter(tz => tz.match(gmtTZCode))[0],
    timezone: moment.tz(gmtTZCode).format('Z'),
    offset: moment.tz(gmtTZCode).utcOffset(),
  })
}

zones.sort((a, b) => a.offset - b.offset)

const options = zones.map(zone => ({
  value: zone.name,
  label: `GMT(${zone.timezone}) ${zone.name}`,
}))

const getTimeZoneLabel = timezone => {
  const zone = zones.find(obj => obj.name === timezone || '')
  return `GMT(${zone?.timezone}) ${zone?.name || ''}`
}

class TimeZone extends React.Component {
  handleChange = selectedOption => {
    const { onChange } = this.props
    onChange(selectedOption)
  }

  render() {
    const {
      classes,
      className,
      customCol,
      customMargin,
      customPadding,
      customWidth,
      disabled,
      errorText,
      fieldLabel,
      fullWidth,
      handleClear,
      helperText,
      id,
      isLabelInline,
      isRequired,
      isToolTip,
      justify,
      label,
      labelIcon,
      noMobile,
      notWrapper,
      noWrap,
      placeholder,
      selectedOption,
      showClear,
      spacing,
      toolTipIcon,
      toolTipPosition,
      toolTipText,
      variant,
      withBorder,
      withDivider,
    } = this.props

    let selectedOptionObject = []

    if (selectedOption && typeof selectedOption !== 'object') {
      selectedOptionObject = [
        {
          value: selectedOption,
          label: getTimeZoneLabel(selectedOption),
        },
      ]
    }

    const DownArrow = () => <ArrowDropDown className={classes.arrow} />

    return (
      <LabelWrapper
        className={cn([classes.timeZone], className)}
        customCol={customCol}
        customMargin={customMargin}
        customPadding={customPadding}
        customWidth={customWidth}
        disabled={disabled}
        errorText={errorText}
        fieldLabel={fieldLabel}
        fullWidth={fullWidth}
        handleClear={handleClear}
        helperText={helperText}
        id={id}
        isLabelInline={isLabelInline}
        isRequired={isRequired}
        isToolTip={isToolTip}
        justify={justify}
        label={label}
        labelIcon={labelIcon}
        noMobile={noMobile}
        notWrapper={notWrapper}
        noWrap={noWrap}
        showClear={showClear}
        spacing={spacing}
        toolTipIcon={toolTipIcon}
        toolTipPosition={toolTipPosition}
        toolTipText={toolTipText}
        variant={variant}
        withBorder={withBorder}
        withDivider={withDivider}
      >
        <Select
          className={cn('tz-select-wrapper', classes.timezoneSelectControl)}
          classNamePrefix="tz-select"
          value={selectedOptionObject}
          onChange={this.handleChange}
          options={options}
          placeholder={placeholder}
          components={{
            DropdownIndicator: DownArrow,
          }}
          isDisabled={disabled}
          menuPortalTarget={document.body}
          styles={customDropDownStyles}
        />
      </LabelWrapper>
    )
  }
}

TimeZone.defaultProps = {
  placeholder: 'Select a timezone',
}

const TimeZoneComponent = withStyles(styles)(TimeZone)
TimeZoneComponent.displayName = 'TimeZone'
export default TimeZoneComponent
